import {
  BUTTON_DEFAULT_PRESET_ID,
  EMAIL_MAKER_BUTTON_PRESETS,
  EMAIL_MAKER_COMMANDS,
  EMAIL_MAKER_COMPONENT_TYPES,
  EMAIL_MAKER_PROPS,
} from '@EmailMakerReact/email-maker-react.constants';
import type {Component, ComponentModelDefinition, ComponentView, StyleProps} from 'grapesjs';
import type {ComponentRegistrationContext} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {asViewEvents, defaultViewEvents} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {normalizeRteEditedHtml} from '@EmailMakerReact/lib/email-maker-rte-content-normalize';

/** Backbone stores the parent prototype on the subclass as `__super__`; the base text view exposes `getContent`. */
interface BaseTextViewProto {
  getContent: (this: ComponentView) => Promise<string>;
}

/** Strips native RTE junk wrappers so resetFromString yields one PMW_TEXT with inline PMW_LINK children. */
async function getNormalizedTextContent(this: ComponentView): Promise<string> {
  // eslint-disable-next-line no-underscore-dangle
  const baseProto = (this.constructor as unknown as {__super__: BaseTextViewProto}).__super__;
  const baseContent = await baseProto.getContent.call(this);
  return normalizeRteEditedHtml(baseContent);
}

/** Preset ids for PMW_BUTTON, mirroring the keys of EMAIL_MAKER_BUTTON_PRESETS. */
const BUTTON_PRESET_ID_1 = 1;
const BUTTON_PRESET_ID_2 = 2;
const BUTTON_PRESET_ID_3 = 3;

/** HTML tag names recognized as PMW_TEXT components. */
const PMW_TEXT_VALID_TAGS = ['P', 'SPAN', 'DIV', 'OL', 'UL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LABEL', 'PRE', 'EM'] as const;

/** Default link presentation, also applied to a raw `<a>` before GrapesJS re-parses it into PMW_LINK on blur. */
export const PMW_LINK_PRESENTATION_STYLE = {
  'text-decoration': 'inherit',
  'border-radius': '0',
} as const;

export function registerTextTypes({
  editor,
  toolbarContainerBtn,
  unstylableEmailStyles,
  dispatchDblClickOnDoubleTap,
  initRemovalOfUnsupportedCssProperties,
  onTextTouchEnd,
  buttonToolbarOptions,
  textToolbarOptions,
}: ComponentRegistrationContext): void {
  const cmpt = editor.Components;
  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK, {
    model: {
      defaults: {
        trackingName: 'Link',
        selectable: false,
        hoverable: false,
        highlightable: false,
        draggable: false,
        droppable: false,
        toolbar: [toolbarContainerBtn],
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          'data-is-pmw-link': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK,
        },
        style: {...PMW_LINK_PRESENTATION_STYLE},
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    view: {
      events: defaultViewEvents(dispatchDblClickOnDoubleTap),
    },
    isComponent: (el: HTMLElement) => {
      if (el.tagName && el.tagName === 'A' && !el.getAttribute('data-is-pmw-social-item')) {
        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK,
        };
      }

      return false;
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.LINK,
    model: {
      defaults: {
        trackingName: 'Button',
        toolbar: buttonToolbarOptions,
        removable: true,
        highlightable: true,
        hoverable: true,
        selectable: true,
        droppable: false,
        draggable: true,
        editable: false,
        name: window.i18next.t('pmwjs_button'),
        unstylable: unstylableEmailStyles,
        badgable: true,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          'data-is-pmw-button': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON,
          class: 'pmw-content-button',
        },
        components: [editor.runCommand(EMAIL_MAKER_COMMANDS.GET_TABLE_COMPONENT_FOR_BUTTON)],
        style: {
          'background-color': '#FD595B',
          'border-radius': '12px',
          color: '#FFFFFF',
          padding: '15px',
          'font-size': '16px',
          'letter-spacing': 'normal',
          'line-height': '20px',
          margin: '0 auto 0 auto',
          width: 'auto',
          'white-space': 'break-spaces',
          'vertical-align': 'middle',
          'text-align': 'center',
          display: 'table',
          'font-family': 'Helvetica, sans-serif',
          opacity: '100%',
          'border-color': '#FFFFFF',
          'border-width': '0px',
          'border-style': 'none',
          'font-style': 'normal',
          'font-weight': 'normal',
          'text-decoration': 'none',
        },
        [EMAIL_MAKER_PROPS.COMPONENT_PRESET]: BUTTON_DEFAULT_PRESET_ID,
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        if (this.get(EMAIL_MAKER_PROPS.COMPONENT_PRESET) === BUTTON_DEFAULT_PRESET_ID) {
          const style = this.getStyle();

          if (typeof style['border-radius'] !== 'undefined') {
            const radius = style['border-radius'] as string;

            if (radius) {
              const radiusNum = parseInt(radius, 10);

              if (radiusNum === EMAIL_MAKER_BUTTON_PRESETS[1]) {
                this.set(EMAIL_MAKER_PROPS.COMPONENT_PRESET, BUTTON_PRESET_ID_1);
              } else if (radiusNum <= EMAIL_MAKER_BUTTON_PRESETS[2]) {
                this.set(EMAIL_MAKER_PROPS.COMPONENT_PRESET, BUTTON_PRESET_ID_2);
              } else if (radiusNum <= EMAIL_MAKER_BUTTON_PRESETS[3]) {
                this.set(EMAIL_MAKER_PROPS.COMPONENT_PRESET, BUTTON_PRESET_ID_3);
              } else {
                this.set(EMAIL_MAKER_PROPS.COMPONENT_PRESET, BUTTON_DEFAULT_PRESET_ID);
              }
            }
          }
        }

        const style = this.getStyle();
        const width = style.width === 'fit-content' ? 'auto' : style.width;

        this.setStyle({...this.defaults.style, ...style, display: 'table', width} as StyleProps);

        this.on('change:style:width', () => {
          if (this.getStyle().width === '100%') {
            editor.runCommand(EMAIL_MAKER_COMMANDS.FIX_COMPONENT_FULL_WIDTH_OVERFLOW_AOL_YAHOO);
          }
        });
      },
    },
    view: {
      onTouchEnd: onTextTouchEnd,
      events: asViewEvents({
        dblclick: 'onActive',
        touchend: 'onTouchEnd',
      }),
    },
    isComponent: (el: HTMLElement) => {
      if (el.tagName && el.tagName === 'A' && el.getAttribute('data-is-pmw-button')) {
        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON,
        };
      }

      return false;
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.TEXT,
    model: {
      defaults: {
        trackingName: 'Text',
        tagName: 'p',
        highlightable: false,
        hoverable: true,
        name: window.i18next.t('pmwjs_text'),
        badgable: true,
        droppable: false,
        toolbar: textToolbarOptions,
        content: window.i18next.t('pmwjs_insert_text'),
        unstylable: [...unstylableEmailStyles, 'background-image'],
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT,
        },
        style: {
          color: '#242424',
          display: 'block',
          padding: '10px',
          margin: '0',
          width: 'unset',
          'box-sizing': 'border-box',
          'word-break': 'break-word',
          'overflow-wrap': 'break-word',
          'letter-spacing': 'normal',
          'font-size': '16px',
          'font-weight': 'normal',
          'line-height': '20px',
          'font-family': 'Helvetica, sans-serif',
          'text-align': 'left',
          opacity: '100%',
          'border-color': '#FFFFFF',
          'border-width': '0px',
        },
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    view: {
      onTouchEnd: onTextTouchEnd,
      getContent: getNormalizedTextContent,
      events: asViewEvents({
        dblclick: 'onActive',
        touchend: 'onTouchEnd',
      }),
    },
    isComponent: (el: HTMLElement) => {
      if (el instanceof Element && el.getAttribute('data-is-readonly-text')) {
        return false;
      }

      if ((PMW_TEXT_VALID_TAGS as readonly string[]).includes(el.tagName)) {
        const contentTexts: string[] = [];

        Array.from(el.childNodes).forEach((node) => {
          if (node.textContent && node.textContent !== '') {
            contentTexts.push(node.textContent);
          }
        });

        const content = contentTexts.join('<br/>');

        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT,
          content,
          components: [],
        };
      }

      return false;
    },
  });
}
